export const REPORT_SCHEMA_VERSION = 1 as const;

export const REPORT_DATA_SOURCES = ["cases"] as const;
export type ReportDataSource = typeof REPORT_DATA_SOURCES[number];

/**
 * Stable public keys shared by report definitions, normalisation and analytics.
 * Additions are schema changes; labels and source fields belong in the registry.
 */
export const DIMENSION_KEYS = [
  "case_date",
  "calendar_year",
  "calendar_month",
  "training_year",
  "day_of_week",
  "session",
  "specialty",
  "procedure",
  "patient_age",
  "age_group",
  "asa",
  "urgency",
  "supervision_level",
  "hospital",
  "funding",
  "nhs_private",
  "anaesthetic_technique",
  "airway_technique",
  "regional_technique",
  "role",
  "supervisor_grade",
  "complications",
  "day_case",
  "practical_procedure",
  "postoperative_destination",
] as const;
export type DimensionKey = typeof DIMENSION_KEYS[number];

export const NUMERIC_FIELD_KEYS = ["duration_minutes"] as const;
export type NumericFieldKey = typeof NUMERIC_FIELD_KEYS[number];

export const METRIC_KEYS = [
  "case_count",
  "percentage_of_cases",
  "distinct_procedures",
  "complication_count",
  "cases_with_complications",
  "average",
  "minimum",
  "maximum",
  "sum",
] as const;
export type MetricKey = typeof METRIC_KEYS[number];

export const REPORT_WIDGET_TYPES = ["chart", "table", "metric", "text"] as const;
export type ReportWidgetType = typeof REPORT_WIDGET_TYPES[number];

export const VISUALISATION_TYPES = [
  "vertical_bar",
  "horizontal_bar",
  "grouped_bar",
  "stacked_bar",
  "line",
  "donut",
  "table",
  "headline_metric",
  "text",
] as const;
export type VisualisationType = typeof VISUALISATION_TYPES[number];

export const FILTER_OPERATORS = [
  "is",
  "is_not",
  "is_any_of",
  "is_known",
  "is_unknown",
  "equals",
  "greater_than",
  "less_than",
  "between",
  "before",
  "after",
  "in_the_last",
  "calendar_year",
  "training_year",
  "yes",
  "no",
] as const;
export type FilterOperator = typeof FILTER_OPERATORS[number];

export type RelativeDateUnit = "days" | "weeks" | "months" | "years";

export interface NumberRangeValue {
  readonly minimum: number;
  readonly maximum: number;
}

export interface DateRangeValue {
  readonly startDate: string;
  readonly endDate: string;
}

export interface RelativeDateValue {
  readonly amount: number;
  readonly unit: RelativeDateUnit;
}

export type ReportFilterValue =
  | string
  | readonly string[]
  | number
  | boolean
  | NumberRangeValue
  | DateRangeValue
  | RelativeDateValue;

interface ReportFilterBase {
  readonly id: string;
  readonly dimension: DimensionKey;
  readonly operator: FilterOperator;
  readonly value?: ReportFilterValue;
}

export interface GlobalReportFilter extends ReportFilterBase {
  readonly scope: "global";
}

export interface WidgetFilter extends ReportFilterBase {
  readonly scope: "widget";
}

export interface StudioReportDateRange {
  /** Inclusive local calendar date in the canonical `yyyy-MM-dd` format. */
  readonly startDate: string;
  /** Inclusive local calendar date in the canonical `yyyy-MM-dd` format. */
  readonly endDate: string;
}

export interface CalculationSort {
  readonly by: "value" | "label";
  readonly direction: "ascending" | "descending";
}

export type PercentageDenominator = "included_cases" | "primary_group";

export interface CalculationDefinition {
  readonly metric: MetricKey;
  /** Required by average/minimum/maximum/sum and ignored by count metrics. */
  readonly numericField?: NumericFieldKey;
  readonly primaryDimension?: DimensionKey;
  readonly secondaryDimension?: DimensionKey;
  readonly percentageDenominator: PercentageDenominator;
  readonly sort: CalculationSort;
  readonly categoryLimit?: number;
  readonly includeEmptyCategories: boolean;
}

/** Library-independent display choice. Chart.js/ECharts options never enter persistence. */
export interface VisualisationDefinition {
  readonly type: VisualisationType;
}

export interface WidgetDisplayOptions {
  readonly showTitle: boolean;
  readonly showDescription: boolean;
  readonly showLegend: boolean;
  readonly showValues: boolean;
  readonly showCounts: boolean;
  readonly showPercentages: boolean;
  readonly showMissingValues: boolean;
}

interface ReportWidgetBase {
  readonly id: string;
  readonly title: string;
  readonly description?: string;
  readonly dataSource: ReportDataSource;
  readonly filters: readonly WidgetFilter[];
  readonly displayOptions: WidgetDisplayOptions;
}

export interface AnalyticalReportWidget extends ReportWidgetBase {
  readonly type: "chart" | "table" | "metric";
  readonly calculation: CalculationDefinition;
  readonly visualisation: VisualisationDefinition;
}

export interface TextReportWidget extends ReportWidgetBase {
  readonly type: "text";
  readonly calculation: null;
  readonly visualisation: { readonly type: "text" };
  readonly textContent: string;
  /** Set only by the migration layer when an obsolete widget cannot be decoded safely. */
  readonly unavailableReason?: string;
}

export type ReportWidget = AnalyticalReportWidget | TextReportWidget;

/** Twelve-column desktop layout; adapters own any grid-library conversion. */
export interface ReportLayoutItem {
  readonly widgetId: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly minimumWidth?: number;
  readonly minimumHeight?: number;
}

export interface StudioReport {
  readonly id: string;
  readonly ownerId: string;
  readonly name: string;
  readonly description?: string;
  readonly schemaVersion: typeof REPORT_SCHEMA_VERSION;
  readonly dataSource: ReportDataSource;
  readonly globalFilters: readonly GlobalReportFilter[];
  readonly dateRange?: StudioReportDateRange;
  readonly widgets: readonly ReportWidget[];
  readonly layout: readonly ReportLayoutItem[];
  /** Canonical ISO-8601 timestamp at the domain boundary. */
  readonly createdAt: string;
  /** Canonical ISO-8601 timestamp at the domain boundary. */
  readonly updatedAt: string;
}

export type DimensionDataType = "date" | "number" | "category" | "boolean";
export type DimensionValue = string | number | boolean | null;

export interface DimensionDefinition<TRecord = unknown> {
  readonly key: DimensionKey;
  readonly label: string;
  readonly description: string;
  readonly dataType: DimensionDataType;
  readonly sourceField?: string;
  readonly derive?: (record: TRecord) => DimensionValue | readonly DimensionValue[];
  readonly filterOperations: readonly FilterOperator[];
  readonly suitableVisualisations: readonly VisualisationType[];
  readonly unknownLabel: string;
  readonly categoryOrder?: readonly string[];
  readonly mayGroupPrimary: boolean;
  readonly mayGroupSecondary: boolean;
  readonly isMultiValue?: boolean;
}

export type MetricAggregationType =
  | "count"
  | "percentage"
  | "distinct_count"
  | "average"
  | "minimum"
  | "maximum"
  | "sum";

export interface MetricFormatting {
  readonly style: "integer" | "decimal" | "percentage" | "duration";
  readonly maximumFractionDigits: number;
  readonly suffix?: string;
}

export interface MetricGroupingRules {
  readonly minimumDimensions: 0 | 1;
  readonly maximumDimensions: 0 | 1 | 2;
}

export interface MetricDefinition {
  readonly key: MetricKey;
  readonly label: string;
  readonly description: string;
  readonly aggregationType: MetricAggregationType;
  readonly eligibleSourceFields: readonly NumericFieldKey[];
  readonly outputDataType: "number" | "percentage";
  readonly formatting: MetricFormatting;
  readonly validGrouping: MetricGroupingRules;
}

export interface AnalyticsRequest {
  readonly dataSource: ReportDataSource;
  readonly globalFilters: readonly GlobalReportFilter[];
  readonly widgetFilters: readonly WidgetFilter[];
  readonly dateRange?: StudioReportDateRange;
  readonly calculation: CalculationDefinition;
  readonly includeSourceRecordIds: boolean;
}

export type AnalyticsCellValue = string | number | boolean | null;

export interface AnalyticsColumn {
  readonly key: string;
  readonly label: string;
  readonly role: "primary_dimension" | "secondary_dimension" | "metric";
  readonly dataType: DimensionDataType | "percentage";
}

export interface AnalyticsResultRow {
  readonly id: string;
  readonly values: Readonly<Record<string, AnalyticsCellValue>>;
  /** Present only when requested; used by owner-scoped drill-down. */
  readonly sourceRecordIds: readonly string[];
}

export interface AnalyticsWarning {
  readonly code:
    | "obsolete_dimension"
    | "unavailable_field"
    | "too_many_categories"
    | "all_values_missing"
    | "invalid_values"
    | "multi_value_percentage"
    | "empty_result";
  readonly message: string;
  readonly field?: DimensionKey | NumericFieldKey;
}

export interface AnalyticsRequestMetadata {
  readonly metric: MetricKey;
  readonly primaryDimension?: DimensionKey;
  readonly secondaryDimension?: DimensionKey;
  readonly percentageDenominator?: PercentageDenominator;
  readonly denominatorCount?: number;
  readonly generatedAt: string;
}

export interface AnalyticsResult {
  readonly columns: readonly AnalyticsColumn[];
  readonly rows: readonly AnalyticsResultRow[];
  readonly totalIncludedRecordCount: number;
  readonly excludedRecordCount: number;
  readonly missingValueCounts: Readonly<Partial<Record<DimensionKey | NumericFieldKey, number>>>;
  readonly requestMetadata: AnalyticsRequestMetadata;
  readonly warnings: readonly AnalyticsWarning[];
}

export const DEFAULT_CALCULATION_SORT: CalculationSort = {
  by: "value",
  direction: "descending",
};

/** Natural order for dates and reporting periods, from earliest to latest. */
export const CHRONOLOGICAL_CALCULATION_SORT: CalculationSort = {
  by: "label",
  direction: "ascending",
};

export const DEFAULT_WIDGET_DISPLAY_OPTIONS: WidgetDisplayOptions = {
  showTitle: true,
  showDescription: true,
  showLegend: true,
  showValues: false,
  showCounts: true,
  showPercentages: false,
  showMissingValues: true,
};

export interface CreateStudioReportOptions {
  readonly id?: string;
  readonly ownerId: string;
  readonly name?: string;
  readonly description?: string;
  readonly now?: string;
  readonly idFactory?: () => string;
}

export interface DuplicateWidgetOptions {
  readonly idFactory?: () => string;
  readonly title?: string;
}

export interface DuplicateReportOptions {
  readonly idFactory?: () => string;
  readonly ownerId?: string;
  readonly name?: string;
  readonly now?: string;
}

function defaultIdFactory(): string {
  return globalThis.crypto.randomUUID();
}

function canonicalNow(value?: string): string {
  return value ?? new Date().toISOString();
}

export function createStudioReport(options: CreateStudioReportOptions): StudioReport {
  const idFactory = options.idFactory ?? defaultIdFactory;
  const now = canonicalNow(options.now);
  return {
    id: options.id ?? idFactory(),
    ownerId: options.ownerId,
    name: options.name?.trim() || "Untitled report",
    ...(options.description?.trim() ? { description: options.description.trim() } : {}),
    schemaVersion: REPORT_SCHEMA_VERSION,
    dataSource: "cases",
    globalFilters: [],
    widgets: [],
    layout: [],
    createdAt: now,
    updatedAt: now,
  };
}

export function duplicateReportWidget(
  widget: ReportWidget,
  options: DuplicateWidgetOptions = {},
): ReportWidget {
  const idFactory = options.idFactory ?? defaultIdFactory;
  const duplicate = structuredClone(widget);
  return {
    ...duplicate,
    id: idFactory(),
    title: options.title?.trim() || `${widget.title} copy`,
    filters: duplicate.filters.map((filter) => ({ ...filter, id: idFactory() })),
  };
}

export function duplicateStudioReport(
  report: StudioReport,
  options: DuplicateReportOptions = {},
): StudioReport {
  const idFactory = options.idFactory ?? defaultIdFactory;
  const now = canonicalNow(options.now);
  const widgetIds = new Map<string, string>();
  const widgets = report.widgets.map((widget): ReportWidget => {
    const id = idFactory();
    widgetIds.set(widget.id, id);
    const duplicate = duplicateReportWidget(widget, { idFactory, title: widget.title });
    return { ...duplicate, id };
  });

  return {
    ...structuredClone(report),
    id: idFactory(),
    ownerId: options.ownerId ?? report.ownerId,
    name: options.name?.trim() || `${report.name} copy`,
    widgets,
    layout: report.layout.flatMap((item) => {
      const widgetId = widgetIds.get(item.widgetId);
      return widgetId ? [{ ...item, widgetId }] : [];
    }),
    createdAt: now,
    updatedAt: now,
  };
}
